/**
 * [AI補完仕様] このコメントはAI補完（コード自動生成）に構造情報として渡されます。変更時は実装品質に直結するため正確に保つこと。
 *
 * 一覧画面向けの共通ページネーションUI（前へ/次へ・ページ番号・総件数表示）。
 *
 * @contract total が limit 以下の場合は何も表示しない（呼び出し側で分岐不要）。
 * @contract onChange には次に表示する offset（0始まり）を渡すこと。ページ番号ではない。
 * @signature Pagination(props: { total: number; limit: number; offset: number; onChange: (offset: number) => void }): JSX.Element | null
 */
interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}

function buildPageNumbers(currentPage: number, totalPages: number): number[] {
  const pages = new Set<number>([1, totalPages]);
  for (let p = currentPage - 1; p <= currentPage + 1; p++) {
    if (p >= 1 && p <= totalPages) pages.add(p);
  }
  return Array.from(pages).sort((a, b) => a - b);
}

export function Pagination({ total, limit, offset, onChange }: PaginationProps) {
  if (total <= limit) return null;

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="pagination" data-testid="pagination">
      <span className="pagination-summary">
        全 {total} 件 / {totalPages} ページ中 {currentPage} ページ目
      </span>
      <div className="pagination-controls">
        <button
          type="button"
          onClick={() => onChange(offset - limit)}
          disabled={offset === 0}
          data-testid="pagination-prev"
        >
          前へ
        </button>
        {buildPageNumbers(currentPage, totalPages)
          .reduce<(number | '...')[]>((acc, p, idx, arr) => {
            if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) {
              acc.push('...');
            }
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="pagination-ellipsis">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onChange(((p as number) - 1) * limit)}
                className={p === currentPage ? 'pagination-page active' : 'pagination-page'}
                data-testid={`pagination-page-${p}`}
              >
                {p}
              </button>
            )
          )}
        <button
          type="button"
          onClick={() => onChange(offset + limit)}
          disabled={offset + limit >= total}
          data-testid="pagination-next"
        >
          次へ
        </button>
      </div>
    </div>
  );
}
